import { useCallback, useContext, useEffect, useState } from 'react';
import SmartConnectorService from 'services/SmartConnectorService';
import {
  DevPackageGenerationResult,
  ScriptExecutionResult,
  ScriptFile,
} from 'pages/SmartConnectors/types';
import { SmartConnectorContext } from '../SmartConnectorWizard/context';
import PerformActionService from 'services/PerformActionService';
import { useToast, toastVariant } from '__components/ToastProvider';
import { useTranslation } from 'react-i18next';
import { downloadFile } from 'utility/DownloadFile';
import { SMART_CONNECTOR_TYPES } from 'pages/SmartConnectors/constants';
import { getOriginalError } from 'services/AxiosService';
import { getErrorMessage } from 'hooks/useErrors';

const POLL_INTERVAL = 3000;

export type UseScriptsProps = {
  pollingScriptExecution: boolean;
  pollingDevPackage: boolean;
  publishingLiveScript: boolean;
  generatingSQLTemplate: boolean;
  handleTestExecution: () => Promise<void>;
  handlePublishLiveScript: () => Promise<boolean>;
  handleChangeSourceFile: (file: ScriptFile) => Promise<void>;
  handleDownloadLocalDevPackage: () => Promise<void>;
  executionResult: ScriptExecutionResult | null;
};

export const useScripts = (): UseScriptsProps => {
  const [showToast] = useToast();
  const { t } = useTranslation();

  const {
    smartConnector,
    stepData,
    setStepData,
    handleUpdate,
    refetchSmartConnector,
    handleSave,
    setForceResetScript,
    setTemplateScript,
  } = useContext(SmartConnectorContext);

  const [executionResult, setExecutionResult] =
    useState<ScriptExecutionResult | null>(null);
  const [polling, setPolling] = useState<boolean>(false);
  const [pollingDevPackage, setPollingDevPackage] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);

  const handleChangeSourceFile = useCallback(
    async (file: ScriptFile) => {
      setGenerating(true);
      try {
        const { data } = await SmartConnectorService.getScriptTemplate(
          smartConnector.id,
          file.id
        );

        if (
          data.sql_required &&
          smartConnector.connector_type === SMART_CONNECTOR_TYPES.spreadsheet
        ) {
          showToast({
            message: t(
              'This SmartConnector has multiple sheets and will require SQL Processing prior to upload.'
            ),
            variant: toastVariant.ALERT,
          });
        }
        setStepData((prev) => ({
          ...prev,
          source_file: file,
          sql_enabled: data.sql_required ? true : prev.sql_enabled,
          sql_required: data.sql_required,
          last_draft_script: {
            ...prev.last_draft_script,
            config_metadata: data.config_metadata,
            // We do not want to override the user_script if it already exists
            user_script:
              prev.last_draft_script?.user_script || data.user_script,
            last_execution_status_id: null,
            sql_version: data.sql_version,
          },
        }));
        setTemplateScript(data.user_script);
        setForceResetScript((prev) => prev + 1);
      } catch (err) {
        const message = getErrorMessage(getOriginalError(err));
        showToast({
          message: message || t('Failed to Generate SQL Template'),
          variant: toastVariant.FAILURE,
        });
      } finally {
        setGenerating(false);
      }
    },
    [
      smartConnector.id,
      smartConnector.connector_type,
      setStepData,
      setForceResetScript,
      showToast,
      setTemplateScript,
      t,
    ]
  );

  const handleTestExecution = useCallback(async () => {
    setPolling(true);
    const [updated, updatedSmartConnector] = await handleSave(false, true);
    try {
      if (updated && updatedSmartConnector) {
        const { data } =
          await SmartConnectorService.startSmartConnectorScriptRun(
            smartConnector.id,
            updatedSmartConnector.last_draft_script?.id,
            {
              user_script: updatedSmartConnector.last_draft_script?.user_script,
              source_file_id: updatedSmartConnector.source_file?.id,
              config_metadata:
                updatedSmartConnector.last_draft_script?.config_metadata,
              sql_version: updatedSmartConnector.last_draft_script?.sql_version,
            }
          );

        if (data.last_execution_status_id) {
          handleUpdate(
            Promise.resolve({
              last_draft_script: {
                ...updatedSmartConnector.last_draft_script,
                user_script:
                  updatedSmartConnector.last_draft_script?.user_script || '',
                last_execution_status_id: data.last_execution_status_id,
              },
            })
          );
        } else {
          throw new Error('Failed to Start Test Script Execution');
        }
      } else {
        setPolling(false);
        if (updated === false) {
          throw new Error('Failed to Start Test Script Execution');
        }
      }
    } catch (err) {
      showToast({
        message: t('Failed to Start Test Script Execution'),
        variant: toastVariant.FAILURE,
      });
      setPolling(false);
    }
  }, [smartConnector.id, handleUpdate, showToast, handleSave, t]);

  const handlePublishLiveScript = useCallback(async () => {
    setPublishing(true);

    try {
      await SmartConnectorService.publishSmartConnectorScript(
        smartConnector.id,
        smartConnector.last_draft_script?.id
      );

      await refetchSmartConnector();
      return true;
    } catch (err) {
      showToast({
        message: t('Failed to Publish Live Script'),
        variant: toastVariant.FAILURE,
      });
      return false;
    } finally {
      setPublishing(false);
    }
  }, [
    smartConnector.id,
    smartConnector.last_draft_script?.id,
    refetchSmartConnector,
    showToast,
    t,
  ]);

  const handleDownloadLocalDevPackage = async () => {
    if (smartConnector.last_draft_script?.id && !pollingDevPackage) {
      setPollingDevPackage(true);
      let timeout: NodeJS.Timeout | undefined;

      const [updated] = await handleSave(false, true);

      try {
        if (updated) {
          const { data } = await SmartConnectorService.generateDevPackage(
            smartConnector.id,
            smartConnector.last_draft_script.id
          );

          await new Promise<void>((resolve, reject) => {
            const poll = async () => {
              try {
                const devPackageResult: DevPackageGenerationResult =
                  await PerformActionService.getBulkActionProgressById(
                    data.progress_status_id
                  );

                if (devPackageResult.status !== 'in_progress') {
                  clearTimeout(timeout);
                  downloadFile(devPackageResult.export_file?.url);
                  resolve();
                } else {
                  timeout = setTimeout(poll, POLL_INTERVAL);
                }
              } catch (err) {
                reject(err);
              }
            };

            poll();
          });
        } else if (updated === false) {
          throw new Error('Failed to download local dev package');
        }
      } catch (err) {
        clearTimeout(timeout);
        showToast({
          variant: toastVariant.FAILURE,
          message: t('Failed to download local dev package'),
        });
      } finally {
        setPollingDevPackage(false);
      }
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    let reset = false;
    setExecutionResult(null);

    if (stepData.last_draft_script?.last_execution_status_id) {
      setPolling(true);

      const poll = async () => {
        const scriptExecutionResult: ScriptExecutionResult =
          await PerformActionService.getBulkActionProgressById(
            stepData.last_draft_script?.last_execution_status_id
          );

        if (reset) {
          return;
        }

        if (scriptExecutionResult.status !== 'in_progress') {
          clearTimeout(timeout);
          setExecutionResult(scriptExecutionResult);
          setPolling(false);
        } else {
          timeout = setTimeout(poll, POLL_INTERVAL);
        }
      };

      poll();
    } else {
      setPolling(false);
    }

    return () => {
      clearInterval(timeout);
      setPolling(false);
      reset = true;
    };
  }, [stepData.last_draft_script?.last_execution_status_id]);

  return {
    pollingScriptExecution: polling,
    pollingDevPackage,
    publishingLiveScript: publishing,
    generatingSQLTemplate: generating,
    handleTestExecution,
    handlePublishLiveScript,
    handleChangeSourceFile,
    handleDownloadLocalDevPackage,
    executionResult,
  };
};
